/**
 * LRU (Least Recently Used) Cache implementation
 * Optimized for chunk caching with O(1) operations using Map's insertion order
 */
import logger from './logger';

class LRUCache {
  constructor(maxSize = 10) {
    this.maxSize = maxSize;
    this.cache = new Map(); // Map maintains insertion order automatically
  }

  get(key) {
    if (this.cache.has(key)) {
      // Move to end (most recently used) with O(1) operations
      const value = this.cache.get(key);
      this.cache.delete(key);  // O(1)
      this.cache.set(key, value);  // O(1) - re-adds at the end
      return value;
    }
    return undefined;
  }

  set(key, value) {
    if (this.cache.has(key)) {
      // Update existing key and move to end
      this.cache.delete(key);  // O(1)
      this.cache.set(key, value);  // O(1) - re-adds at the end
    } else {
      // Add new key
      if (this.cache.size >= this.maxSize) {
        // Evict least recently used (first item in Map)
        const lru = this.cache.keys().next().value;  // O(1) - get first key
        this.cache.delete(lru);  // O(1)
        logger.log(`🗑️ LRU Cache: Evicted chunk ${lru} (cache full)`);
      }
      
      this.cache.set(key, value);  // O(1)
      logger.log(`💾 LRU Cache: Stored chunk ${key} (${this.cache.size}/${this.maxSize})`);
    }
  }

  has(key) {
    return this.cache.has(key);  // O(1)
  }

  delete(key) {
    return this.cache.delete(key);  // O(1) - returns true if deleted, false otherwise
  }

  clear() {
    this.cache.clear();  // O(1)
  }

  size() {
    return this.cache.size;
  }

  // Iterator methods to match Map interface (fixes runtime crashes in api.js)
  entries() {
    return this.cache.entries();
  }

  values() {
    return this.cache.values();
  }

  keys() {
    return this.cache.keys();
  }

  // Get cache statistics
  getStats() {
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      usage: ((this.cache.size / this.maxSize) * 100).toFixed(1) + '%',
      keys: Array.from(this.cache.keys())
    };
  }
}

export default LRUCache;